// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the files a chat message brings to "Send to an agent" (a Konvo chat's attachments, a
// WhatsApp photo or voice note), fetched a few at a time. The new agent chat takes them like files
// the person added, so the agent sees what the person sees.

import {AGENT_UPLOAD_MAX_BYTES} from '@fluxer/schema/src/domains/konvo/Agents';

export interface AgentSourceFile {
	name: string;
	/** The MIME type the source gave, for a download that names none (or only "octet-stream"). */
	type: string;
	/** A name made up here ("WhatsApp Image … .jpg"): its extension follows the type the download has. */
	madeUpName?: boolean;
	/** Bytes when known ahead: a file larger than an agent takes is left out without fetching it. */
	size: number | null;
	load: () => Promise<Blob | null>;
}

/** Files fetched at once. */
const CONCURRENCY = 4;

const EXTENSIONS: Record<string, string> = {
	'image/jpeg': 'jpg',
	'image/svg+xml': 'svg',
	'audio/mpeg': 'mp3',
	'audio/mp4': 'm4a',
	'video/quicktime': 'mov',
	'text/plain': 'txt',
};

/** A file extension for a MIME type ("audio/ogg; codecs=opus" → "ogg"), or null when it names none. */
export function extensionFor(type: string): string | null {
	const base = type.split(';')[0]!.trim().toLowerCase();
	if (EXTENSIONS[base]) return EXTENSIONS[base]!;
	const sub = base.split('/')[1]?.replace(/^x-/, '').replace(/\+.*$/, '');
	return sub && /^[a-z0-9]{1,8}$/.test(sub) && base !== 'application/octet-stream' ? sub : null;
}

/** The name, or "name (2).ext" when an earlier file has it. */
export function uniqueName(name: string, used: Set<string>): string {
	let next = name;
	for (let count = 2; used.has(next.toLowerCase()); count++) {
		const dot = name.lastIndexOf('.');
		next = dot > 0 ? `${name.slice(0, dot)} (${count})${name.slice(dot)}` : `${name} (${count})`;
	}
	used.add(next.toLowerCase());
	return next;
}

/** The same files with names made unique ("photo (2).jpg"), so the names the agent reads match the files. */
export function uniqueSources(sources: ReadonlyArray<AgentSourceFile>): Array<AgentSourceFile> {
	const used = new Set<string>();
	return sources.map((source) => {
		const name = uniqueName(source.name, used);
		return name === source.name ? source : {...source, name};
	});
}

/** The files in order, without those that failed or are larger than an agent takes; names made unique. */
export async function fetchAgentFiles(sources: ReadonlyArray<AgentSourceFile>): Promise<Array<File>> {
	const wanted = sources.filter((source) => source.size == null || source.size <= AGENT_UPLOAD_MAX_BYTES);
	const fetched: Array<File | null> = new Array(wanted.length).fill(null);
	let next = 0;
	const worker = async () => {
		while (next < wanted.length) {
			const at = next++;
			const source = wanted[at]!;
			const blob = await source.load().catch(() => null);
			if (!blob || blob.size > AGENT_UPLOAD_MAX_BYTES) continue;
			const own = blob.type.split(';')[0]!.trim();
			const type = (own && own !== 'application/octet-stream' ? blob.type : source.type) || 'application/octet-stream';
			const extension = source.madeUpName && own ? extensionFor(own) : null;
			const name = extension ? `${source.name.replace(/\.[a-z0-9]{1,8}$/i, '')}.${extension}` : source.name;
			fetched[at] = new File([blob], name, {type});
		}
	};
	await Promise.all(Array.from({length: Math.min(CONCURRENCY, wanted.length)}, worker));
	const used = new Set<string>();
	const files: Array<File> = [];
	for (const file of fetched) {
		if (!file) continue;
		const name = uniqueName(file.name, used);
		files.push(name === file.name ? file : new File([file], name, {type: file.type}));
	}
	// More than the agent takes: the new chat keeps the first and says so.
	return files;
}
